import { useState } from 'react'
import { ChevronRight, Search } from 'lucide-react'
import { MARKERS, MARKER_GROUPS, isSideMarker, isTeamMarker, teamSrc, type MarkerGroup, type TeamId } from './markers'
import { TeamField, editorInput, editorLabel } from './editorUi'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Seçili takım bir sonraki simge için de geçerli kalır: sağ panel yerleştirmeden
 * sonra özelliklere döner ve bu bileşen sökülür, state'te dursa aynı takımdan beş
 * araç koymak beş kez yeniden seçmek demek olurdu. Oturumluk, varsayılan beyaz.
 */
let lastTeam: TeamId | null = null

/** İlk açılışta en sık kullanılan iki grup açık; sonrası kişinin kendi seçimi. */
const GROUPS_KEY = 'tll_marker_groups'
const DEFAULT_OPEN: MarkerGroup[] = ['tactical', 'vehicles']

function readOpenGroups(): MarkerGroup[] {
  try {
    const raw = localStorage.getItem(GROUPS_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed : DEFAULT_OPEN
  } catch {
    return DEFAULT_OPEN
  }
}

/**
 * Simge seçici — sağ panelin "işaretçi" aracındaki gövdesi.
 *
 * İki şey değişti:
 *
 * - **Adlar basılır.** Simgeler yalnızca ikonla duruyordu ve hangisinin
 *   hangi şey olduğu ancak üstüne gelip beklemekle öğreniliyordu; ipucu metni
 *   bir liste değildir.
 * - **Arama var.** Kutuları gözle taramak, adı bilinen bir simge için
 *   en yavaş yol. Eşleşme İngilizce ad üzerinden yapılır çünkü katalog kod içi
 *   kimliktir (bkz. `markers.ts`).
 *
 * Bileşenin bir zamanlar iki kılığı vardı (`embedded` ve kendi kenar çubuğu);
 * ikincisi hiçbir yerden çizilmiyordu, o yüzden yalnızca gövde kaldı.
 */
export default function MarkersSidebar({ selectedMarkerId, onSelect }: {
  selectedMarkerId: string | null
  onSelect: (src: string, id: string) => void
}) {
  const { T } = useLanguage()
  const [query, setQuery] = useState('')
  const [team, setTeam] = useState<TeamId | null>(lastTeam)
  const [openGroups, setOpenGroups] = useState<MarkerGroup[]>(readOpenGroups)

  const toggleGroup = (g: MarkerGroup) => {
    const next = openGroups.includes(g) ? openGroups.filter(x => x !== g) : [...openGroups, g]
    setOpenGroups(next)
    try { localStorage.setItem(GROUPS_KEY, JSON.stringify(next)) } catch { /* yalnızca kolaylık */ }
  }

  const changeTeam = (next: TeamId | null) => {
    lastTeam = next
    setTeam(next)
    // Elde tutulan simge de yeni renge geçer; tuvalde önizlemesi duruyor.
    const held = MARKERS.find(m => m.id === selectedMarkerId)
    if (held && isTeamMarker(held.src)) onSelect(teamSrc(held.src, next), held.id)
  }

  const q = query.trim().toLowerCase()
  const shown = q ? MARKERS.filter(m => m.label.toLowerCase().includes(q)) : MARKERS

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-white/10 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={T.strategy.searchMarker}
            className={`${editorInput} pl-7`}
          />
        </div>
        <div className="mt-3">
          <TeamField label={T.strategy.team} noneLabel={T.strategy.teamNone} value={team} onChange={changeTeam} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {/* Gruplar açılır kapanır. Arama sürerken hepsi açıktır: sonucu kapalı
            bir grubun altında saklamak aramayı boşa çıkarırdı. */}
        {MARKER_GROUPS.map(g => {
          const items = shown.filter(m => m.group === g)
          if (items.length === 0) return null
          const open = !!q || openGroups.includes(g)
          return (
            <div key={g} className="mb-3 last:mb-0">
              <button
                type="button"
                onClick={() => toggleGroup(g)}
                disabled={!!q}
                aria-expanded={open}
                className="mb-2 flex w-full cursor-pointer items-center gap-1 text-left disabled:cursor-default"
              >
                <ChevronRight className={`h-3 w-3 shrink-0 text-muted-foreground/70 transition-transform ${open ? 'rotate-90' : ''}`} />
                <span className={`flex-1 ${editorLabel}`}>{T.strategy.markerGroups[g]}</span>
                <span className="text-[10px] text-muted-foreground/50">{items.length}</span>
              </button>
              {open && (
                <div className="grid grid-cols-4 gap-1.5">
                  {items.map(m => (
                    <button
                      key={m.id}
                      onClick={() => onSelect(teamSrc(m.src, team), m.id)}
                      title={m.label}
                      className={`flex cursor-pointer flex-col items-center gap-1 rounded-sm border px-1 py-1.5 transition-colors ${
                        selectedMarkerId === m.id
                          ? 'border-primary/60 bg-primary/15'
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <img
                        src={teamSrc(m.src, team)}
                        alt=""
                        aria-hidden
                        className={`h-6 object-contain ${isSideMarker(m.src) ? 'w-full' : 'w-6'}`}
                      />
                      {/* Ad oyunun terimi, çevrilmez, büyütülmez. */}
                      <span className="w-full truncate text-center text-[9px] leading-tight text-muted-foreground">
                        {m.label}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
        {shown.length === 0 && (
          <p className="pt-2 text-xs text-muted-foreground/60">{T.strategy.library.noSearchResult}</p>
        )}
      </div>
    </div>
  )
}
